import { Badge, Button, Space, Typography } from 'antd';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckSquareOutlined,
  DollarOutlined,
  UserAddOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { AlertType, Notification } from '@/types/notification';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import './NotificationItem.css';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Text } = Typography;

const ALERT_ICON_MAP: Record<string, React.ReactNode> = {
  milestone_overdue: <ClockCircleOutlined style={{ color: '#cf1322' }} />,
  deadline_warning: <ClockCircleOutlined style={{ color: '#fa8c16' }} />,
  critical_risk: <ExclamationCircleOutlined style={{ color: '#cf1322' }} />,
  pending_approval: <CheckSquareOutlined style={{ color: '#1890ff' }} />,
  scope_change: <DollarOutlined style={{ color: '#722ed1' }} />,
  item_assigned: <UserAddOutlined style={{ color: '#52c41a' }} />,
};

function getAlertIcon(alertType?: AlertType | string | null): React.ReactNode {
  if (alertType && alertType in ALERT_ICON_MAP) {
    return ALERT_ICON_MAP[alertType];
  }
  return <BellOutlined style={{ color: '#8c8c8c' }} />;
}

function formatRelativeTime(dateStr?: string | null): string {
  if (!dateStr) return '';
  const date = dayjs(dateStr);
  if (!date.isValid()) return '';
  const diffDays = dayjs().diff(date, 'day');
  if (diffDays > 7) return date.format('DD/MM/YYYY HH:mm');
  return date.fromNow();
}

interface Props {
  notification: Notification;
  onClick: () => void;
  isLoading?: boolean;
  isObsolete?: boolean;
  deeplinkPath?: string | null;
}

export function NotificationItem({ notification, onClick, isLoading = false, isObsolete = false }: Omit<Props, 'deeplinkPath'>) {
  const handleClick = () => {
    onClick();
  };

  // Split message at colon for better formatting
  const formatMessage = (msg: string) => {
    const colonIndex = msg.indexOf(':');
    if (colonIndex === -1) return [msg];
    return [
      msg.substring(0, colonIndex + 1), // "Nova tarefa atribuída:"
      msg.substring(colonIndex + 1).trim() // "Notification"
    ];
  };

  const [messagePrefix, messageContent] = formatMessage(notification.message || 'Notificação');

  return (
    <Button
      type="text"
      className={`notification-item ${isObsolete ? 'notification-obsolete' : ''}`}
      onClick={handleClick}
      loading={isLoading}
      block
    >
      <Space direction="horizontal" size={10} align="start" style={{ width: '100%' }}>
        <div style={{ fontSize: 18, lineHeight: 1, paddingTop: 2 }}>
          {isObsolete
            ? <span className="notification-obsolete-icon" aria-label="Recurso removido">⚠️</span>
            : getAlertIcon(notification.alert_type)}
        </div>
        <Space direction="vertical" size={2} align="start" style={{ flex: 1, overflow: 'hidden' }}>
          <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <Text strong style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {messagePrefix}
              </Text>
              {messageContent && (
                <Text style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word', marginTop: 2 }}>
                  {messageContent}
                </Text>
              )}
            </div>
            {!notification.is_read && <Badge status="processing" text="Nova" style={{ flexShrink: 0 }} />}
          </Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {formatRelativeTime(notification.created_at)}
          </Text>
        </Space>
      </Space>
    </Button>
  );
}
